import React, { useState } from 'react';
import { ViewQuilt } from '@mui/icons-material';
import { Tabs, Tab, Box } from '@mui/material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage from '../../Commun/components/GenericPage';
import TemplateLibrary, { Template } from '../components/TemplateLibrary';
import { Button, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import RootDialogPage from '../../Commun/components/RootDialogPage';

import TemplateForm from '../components/TemplateForm';
import { templatesApi } from '../services/templatesApi';
import { ShapeType, WindowType } from '../services/windowsApi';
import { DoorTypeEnum } from '../services/doorsApi';

interface TemplateManagerProps {
    onModeChange: (mode: 'window' | 'door') => void;
}

const TemplateManager: React.FC<TemplateManagerProps & { onEditTemplate: (t: Template) => void, refreshTrigger: number }> = ({ onModeChange, onEditTemplate, refreshTrigger }) => {
    const { t } = useLanguage();
    const [mode, setMode] = useState<'window' | 'door'>('window');

    const handleTabChange = (event: React.SyntheticEvent, newValue: 'window' | 'door') => {
        setMode(newValue);
        onModeChange(newValue);
    };

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={mode} onChange={handleTabChange} aria-label="template types">
                    <Tab label={t('maker.design.tab.windows', 'Windows')} value="window" />
                    <Tab label={t('maker.design.tab.doors', 'Doors')} value="door" />
                </Tabs>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <TemplateLibrary
                    key={refreshTrigger}
                    onSelectTemplate={(t) => console.log(t)}
                    designMode={mode}
                    onEditTemplate={onEditTemplate}
                />
            </Box>
        </Box>
    );
}

const TemplatesPage: React.FC = () => {
    const { t } = useLanguage();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [selectedMode, setSelectedMode] = useState<'window' | 'door'>('window');
    const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
    const [formData, setFormData] = useState<Partial<Template>>({});
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const handleCreate = () => {
        setEditingTemplate(null);
        setFormData({
            name: '',
            description: '',
            defaultWidth: 1000,
            defaultHeight: 2000,
            windowType: WindowType.Casement,
            doorType: DoorTypeEnum.Single
        });
        setCreateDialogOpen(true);
    };

    const handleEdit = (template: Template) => {
        setEditingTemplate(template);
        setFormData(template);
        setCreateDialogOpen(true);
    }

    const handleSave = async () => {
        if (!formData.name) return;

        if (editingTemplate) {
            await templatesApi.update({ ...editingTemplate, ...formData } as Template);
        } else {
            await templatesApi.create({
                ...formData,
                type: selectedMode,
                shapeType: ShapeType.Rectangular, // Default
                icon: selectedMode === 'window' ? '🪟' : '🚪'
            } as Template);
        }
        setCreateDialogOpen(false);
        setRefreshTrigger(prev => prev + 1);
    };

    return (
        <GenericPage
            title={t('menu.maker.templates')}
            icon={<ViewQuilt />}
            footerEnabled={false}
            additionalActions={
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleCreate}
                >
                    {t('common.actions.new')}
                </Button>
            }
        >
            <TemplateManager
                onModeChange={setSelectedMode}
                onEditTemplate={handleEdit}
                refreshTrigger={refreshTrigger}
            />

            <RootDialogPage
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                title={editingTemplate ? t('common.actions.edit') : t('common.actions.new')}
                onAction={handleSave}
                actionLabel={t('common.actions.save')}
            >
                <TemplateForm
                    formData={formData}
                    onChange={setFormData}
                    designMode={selectedMode}
                />
            </RootDialogPage>
        </GenericPage>
    );
};

export default TemplatesPage;
